import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, Users } from 'lucide-react';
import { api, ApiError, type UserSummary } from '../api/client.ts';
import { useChat } from '../chat/ChatContext.tsx';
import { Avatar } from '../components/Avatar.tsx';
import './SettingsScreen.css';
import './NewGroupScreen.css';

/**
 * «Новая группа» — pick the members, then name it.
 *
 * Two steps of one screen rather than two routes, because the whole point of
 * the back chevron on the second one is that it returns to a selection that is
 * still there. A route would have to carry that selection through history to
 * say the same thing.
 */
export function NewGroupScreen() {
  const navigate = useNavigate();
  const { createGroup } = useChat();

  const [step, setStep] = useState<'members' | 'name'>('members');
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  function toggle(id: string) {
    setSelected((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  }

  async function create() {
    const name = title.trim();
    if (!name || selected.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const chat = await createGroup(name, selected);
      // replace, so the browser's back button lands on the chat list rather
      // than on the form of a group that now exists.
      navigate(`/chat/${chat.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать группу');
      setBusy(false);
    }
  }

  const selectedUsers = selected
    .map((id) => users?.find((user) => user.id === id))
    .filter((user): user is UserSummary => user !== undefined);

  return (
    <div className="app-frame settings-screen">
      <header className="settings-header settings-header-divided">
        <button
          type="button"
          className="icon-button settings-back"
          onClick={() => (step === 'name' ? setStep('members') : navigate('/'))}
          aria-label="Назад"
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className="settings-heading group-heading">
          {step === 'members' ? 'Новая группа' : 'Название группы'}
        </h2>

        {step === 'members' ? (
          <button
            type="button"
            className="pill-button"
            disabled={selected.length === 0}
            onClick={() => setStep('name')}
          >
            {/* The count is the reassurance that the taps registered, on a
                list long enough to scroll away from what was picked. */}
            Далее{selected.length > 0 ? ` (${selected.length})` : ''}
          </button>
        ) : (
          <button
            type="button"
            className="pill-button"
            disabled={title.trim().length === 0 || busy}
            onClick={() => void create()}
          >
            Создать
          </button>
        )}
      </header>

      {step === 'members' ? (
        <div className="group-list">
          {users === null ? (
            <div className="group-empty" />
          ) : users.length === 0 ? (
            <div className="group-empty">Больше никого нет</div>
          ) : (
            users.map((user) => {
              const checked = selected.includes(user.id);
              return (
                <button
                  key={user.id}
                  type="button"
                  className="group-pick-row"
                  onClick={() => toggle(user.id)}
                  aria-pressed={checked}
                >
                  <Avatar
                    className="group-pick-avatar"
                    photoId={user.avatarPhotoId}
                    name={user.displayName}
                  />
                  <span className="group-pick-name">{user.displayName}</span>
                  {/* Said out loud, because picking one is not the same act as
                      picking a person: a group with a bot in it is a group
                      subscribed to whatever that bot delivers. */}
                  {user.bot && <span className="group-pick-bot">бот</span>}
                  <span className={`group-check${checked ? ' group-check-on' : ''}`}>
                    {checked && <Check size={13} strokeWidth={3} />}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : (
        <div className="group-name-body">
          <div className="group-name-row">
            {/* Where the handoff puts a camera button. There is nowhere to
                send a group photo yet, so the tile says what the group is
                rather than offering something that would not work. */}
            <div className="group-name-avatar">
              <Users size={22} />
            </div>
            <input
              className="text-field group-name-field"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Название группы"
              aria-label="Название группы"
              maxLength={60}
              autoFocus
            />
          </div>

          <div className="group-section-label">Участники ({selectedUsers.length})</div>
          <div className="group-chips">
            {selectedUsers.map((user) => (
              <div key={user.id} className="group-chip">
                <Avatar
                  className="group-chip-avatar"
                  photoId={user.avatarPhotoId}
                  name={user.displayName}
                />
                <span className="group-chip-name">{user.displayName}</span>
              </div>
            ))}
          </div>

          {error && <div className="form-error">{error}</div>}
        </div>
      )}
    </div>
  );
}
